import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {isEqual} from "@pg-mono/nodash";

import {authFailedButUnlockPrices} from "../../auth/actions/auth_failed_but_unlock_prices_event";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorInstalmentEventAction} from "../../credit_calculator/tracking/credit_gtm";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {setSentLeadValues} from "../../lead/reducers/sent_lead_reducer";
import {IRealEstateIds} from "../../lead/types/IRealEstateIds";
import {calculateRoomsField as calculateRoomsFieldUtil} from "../../lead/utils/calculate_room_fields";
import {getLeadAnalyticsData} from "../../lead/utils/get_lead_analytics_data";
import {getParsedLeadFormValues} from "../../lead/utils/get_parsed_lead_form_values";
import {sendLead} from "../../lead/utils/send_lead";
import {getSentLeadFormValuesFromStorage} from "../../lead/utils/sent_lead_form_values";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {hitGTMEcommerceAddToCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {IAppDispatch, IRootState} from "../../store/store";
import {hitRotatingApplicationError} from "../../tracking/algolytics_hits/application_hit";
import {gtmCheckout} from "../../tracking/google_tag_manager/gtm_checkout";
import {gtmProductAdd} from "../../tracking/google_tag_manager/gtm_product_add";
import {selectGetIsUserAuthenticated} from "../../user/utils/select_get_is_user_authenticated";
import {selectUserProfileData} from "../../user/utils/select_user_profile_data";
import {resetApplicationPost} from "../actions/application_form_actions";
import {initialEmptyApplicationValues} from "../constants/application_form";
import {useApplicationVariant} from "../hooks/use_application_variant";
import {IApplicationData} from "../reducers/application_reducer";
import {hitLeadSent} from "../tracking/hit_lead_sent";
import {IApplicationAnalyticsData, openApplicationFormHit} from "../tracking/tracking";
import {ApplicationVariant} from "../types/ApplicationVariant";
import {IApplication} from "../types/IApplication";
import {IApplicationFormValues} from "../types/IApplicationFormValues";
import {MessageType} from "../types/MessageType";
import {alertFormErrorMessage, alertFormSuccessMessage} from "../utils/alert_text";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getGeneratedMessage} from "../utils/text_generator/get_generated_message";
import {ApplicationForm} from "./application_form/ApplicationForm";
import {IApplicationAdditionalQuestionTypes} from "./application_form/ApplicationFormContext";

type IApplicationOtherProps = {
    initialLeadFormValues: IApplicationFormValues;
    leadData: IApplicationData;
    onSuccessfulSubmit?: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedParams?: Record<string, string>
    ) => void;
    storeHash: string;
    // application input data
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    alwaysShowFields?: boolean;
    hideRodo?: boolean;
    className?: string;
    forceClose?: boolean; // close without any additional modals
};

export type IApplicationOwnProps = IRealEstateIds & IApplicationOtherProps;

type IApplicationProps = IApplicationOwnProps & {
    closeModal: (forceClose?: boolean) => void;
    customFormText?: string;
};

export const Application: React.FC<IApplicationProps> = (props) => {
    const dispatch = useDispatch<IAppDispatch>();
    const location = useLocation();
    const applicationVariant = useApplicationVariant();

    // Redux selectors
    const {affiliation, creditPayload, salesOfficeId, hideQuestions: hideQuestionsFromState} = useAppSelector((state) => state.mainLead);
    const hideQuestions = hideQuestionsFromState || props.leadData.offer?.detail?.region.country !== Country.POLAND;
    const userPreferencesFilters = useSelector((state: IRootState) => state.user.preferences?.data?.filters || {});
    const isAuthenticated = useSelector(selectGetIsUserAuthenticated);
    const userProfileData = useSelector(selectUserProfileData);
    const viewType = useSelector((state: IRootState) => state.viewType.current);

    // Local state
    const [formPreviouslySent, setFormPreviouslySent] = useState(false);
    const [initialFormState, setInitialFormState] = useState<IApplicationFormValues>(props.initialLeadFormValues);

    const [propertyPriceState] = useState<{showPropertyPriceModal: boolean; userDecision: null}>({
        showPropertyPriceModal: false,
        userDecision: null
    });
    const [questions, setQuestions] = useState<IApplicationAdditionalQuestionTypes[]>([]);
    const [messageType, setMessageType] = useState<MessageType>(hideQuestions ? MessageType.CUSTOM : MessageType.GENERATED);
    const [leadIsSubmitting, setLeadIsSubmitting] = useState(false);

    const afterSuccessTimeoutRef = useRef<number>(0);
    const wasInteractionHitRef = useRef(false);

    // Helper functions
    const calculateRoomsField = useCallback(
        (offer: IOfferDetailApplication | undefined) => {
            return calculateRoomsFieldUtil({
                offer,
                propertyId: props.propertyId,
                userPreferencesFilters
            });
        },
        [props.propertyId, userPreferencesFilters]
    );

    const getClearFormState = useCallback(
        (offer: IOfferDetailApplication | undefined) => {
            return {
                initialFormState: {
                    ...initialEmptyApplicationValues,
                    rooms: calculateRoomsField(offer),
                    text: props.customFormText
                }
            };
        },
        [calculateRoomsField, props.customFormText]
    );

    const getAnalyticsData = useCallback((): IApplicationAnalyticsData => {
        return getLeadAnalyticsData(props.leadData, viewType, props.source, props.sourceSection);
    }, [props.leadData, viewType, props.source, props.sourceSection]);

    const onInteraction = useCallback(
        (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
            if (!wasInteractionHitRef.current) {
                wasInteractionHitRef.current = true;

                gtmCheckout(props.leadData, viewType, "standard");
                hitGTMEcommerceBeginCheckout(props.leadData, viewType, "standard", props.sourceSection, formFillAction);
            }
        },
        [props.leadData, viewType, props.sourceSection]
    );

    const onSubmit = useCallback(
        async (values: IApplicationFormValues, applicationData: IApplicationData) => {
            // TODO: Until we rewrite/remove Application, keep this inline with use_lead_modal_send_main_lead
            return await sendLead({
                dispatch: dispatch,
                leadFormValues: values,
                realEstateIds: {
                    vendorId: props.vendorId,
                    offerId: props.offerId,
                    propertyId: props.propertyId,
                    salesOfficeId: salesOfficeId || undefined
                },
                storeHash: props.storeHash,
                leadMeta: {
                    source: props.source,
                    sourceSection: props.sourceSection,
                    locationHash: location.hash,
                    viewType: viewType
                },
                leadType: "standard",
                leadData: applicationData,
                isAuthenticated: isAuthenticated,
                userProfileData: userProfileData,
                senderState: {
                    generatedMessage: null,
                    questions: questions
                },
                analyticsData: getAnalyticsData(),
                affiliation: affiliation || undefined,
                creditData: creditPayload,
                onSendSuccess: async (responseApplication) => {
                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant !== ApplicationVariant.CREDIT) {
                        const hasMktOptin = Boolean(responseApplication.marketing_email || responseApplication.marketing_sms);
                        hitLeadSent(hasMktOptin);
                    }

                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant === ApplicationVariant.CREDIT) {
                        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                            credit_action: CreditCalculatorInstalmentEventAction.CALL
                        });
                    }

                    // Trigger the authentication procedure after sending the application, including setting appropriate A/B test variants
                    dispatch(authFailedButUnlockPrices());

                    props.onSuccessfulSubmit?.(responseApplication, applicationData.offer?.detail, applicationData.property?.detail);
                },
                postOptions: {
                    on400ApplicationBlockedError: (error) => {
                        props.closeModal(true);
                        addWarningNotification("Przepraszamy, oferta na którą chcesz wysłać wiadomość została zarchiwizowana.", 7000);
                        hitRotatingApplicationError(props.source, props.sourceSection, applicationData, {
                            appError: error,
                            extraPayloadViewType: viewType ?? undefined
                        });
                        // TODO: remove this bugsnag notification when we confirm that blocking applications this way is not an issue
                        notifyBugsnag({name: "Application blocked info", message: "Application blocked info"}, "Application sent on an archived offer", {
                            error
                        });
                        setTimeout(() => window.location.reload(), 6000);
                    },
                    on400Response: (error) => {
                        hitRotatingApplicationError(props.source, props.sourceSection, applicationData, {
                            appError: error,
                            extraPayloadViewType: viewType ?? undefined
                        });
                    }
                }
            });
        },
        [dispatch, props, location.hash, viewType, isAuthenticated, userProfileData, questions, getAnalyticsData]
    );

    const onFormikSubmit: IFormikSubmitFn<IApplicationFormValues> = useCallback(
        (values, _formikHelpers) => {
            setLeadIsSubmitting(true);

            // TODO: Until we rewrite/remove Application, keep this inline with use_lead_modal_send_main_lead
            const {offer, property} = props.leadData;

            const offerName = offer?.detail ? offer.detail.name : "";

            const completedValues = {
                ...values,
                text:
                    props.customFormText ||
                    values.text ||
                    getGeneratedMessage({
                        offerType: offer?.detail?.type,
                        offerName: offerName,
                        propertyNumber: property?.detail?.number,
                        name: values.name,
                        rooms: property?.detail?.rooms,
                        area: property?.detail?.area
                    })
            };

            const parsedLeadFormValues = getParsedLeadFormValues(completedValues);
            dispatch(
                setSentLeadValues({
                    ...initialEmptyApplicationValues,
                    ...parsedLeadFormValues,
                    questions: questions
                })
            );

            handleFormikPost(onSubmit(completedValues, props.leadData), _formikHelpers, {
                onSuccess: () => {
                    addSuccessNotification(alertFormSuccessMessage);
                    setLeadIsSubmitting(false);
                    setInitialFormState(getClearFormState(props.leadData.offer?.detail).initialFormState);
                },
                onFieldError: (fieldErrors) => {
                    setLeadIsSubmitting(false);
                    if (!fieldErrors?.email && !fieldErrors?.phone) {
                        addWarningNotification(alertFormErrorMessage);
                    }
                },
                onNonFieldError: () => {
                    setLeadIsSubmitting(false);
                    addWarningNotification(alertFormErrorMessage);
                }
            });
        },
        [props.leadData, props.customFormText, questions, dispatch, onSubmit, addSuccessNotification, getClearFormState, addWarningNotification]
    );

    // componentDidMount
    useEffect(() => {
        const sentLeadFormValues = getSentLeadFormValuesFromStorage();

        // TODO: check formPreviouslySent across application, probably sentLead from redux should be used instead

        setFormPreviouslySent(Boolean(sentLeadFormValues));

        // track form opening
        openApplicationFormHit(getAnalyticsData());
        gtmProductAdd(props.leadData, viewType, "standard");
        hitGTMEcommerceAddToCart(props.leadData, viewType, "standard", props.sourceSection);

        const {name, email, phone} = initialFormState;
        if (name || email || phone?.number) {
            // form will be filled with data automatically
            onInteraction("autofill");
        }
    }, []);

    // componentDidUpdate
    useEffect(() => {
        const equal = isEqual(props.initialLeadFormValues, initialFormState);
        if (!equal) {
            setInitialFormState(props.initialLeadFormValues);
        }
    }, [props.initialLeadFormValues]);

    // componentWillUnmount
    useEffect(() => {
        return () => {
            dispatch(resetApplicationPost(props.storeHash));
            clearTimeout(afterSuccessTimeoutRef.current);
            afterSuccessTimeoutRef.current = -1; // do not allow to setTimeout after this assignment
        };
    }, [props.storeHash]);

    const {showPropertyPriceModal} = propertyPriceState;

    return (
        <div className={scrollFormWrapStyle}>
            {initialFormState && (
                <div className={showPropertyPriceModal ? propertyPriceOverlayStyle : ""}>
                    {/* hideRooms: we show rooms in right column (in form) when we do not ask for property and we consider promotion or vendor (no offerId) application*/}
                    <div className={contentWrapperStyle}>
                        <ApplicationForm
                            leadIsSubmitting={leadIsSubmitting}
                            onSubmit={onFormikSubmit}
                            initialValues={initialFormState}
                            onFormControlClick={onInteraction}
                            formPreviouslySent={formPreviouslySent}
                            hideRooms={!!props.propertyId}
                            hideMessageSection={false}
                            offerId={props.offerId}
                            alwaysShowFields={props.alwaysShowFields} // For this test - when form is in modal, we do not hide fields.
                            storeHash={props.storeHash}
                            hideSubmitButton={false}
                            source={props.source}
                            sourceSection={props.sourceSection}
                            hideRodo={props.hideRodo}
                            applicationMessageLogic={{
                                currentState: {
                                    selectedQuestions: questions,
                                    messageType: messageType
                                },
                                helpers: {
                                    setMessageType: (newMessageType: MessageType) => {
                                        setMessageType(newMessageType);
                                    },
                                    setSelectedQuestions: (questionsIds: IApplicationAdditionalQuestionTypes[]) => {
                                        setQuestions(questionsIds);
                                    }
                                }
                            }}
                            getAnalyticsData={getAnalyticsData}
                            hideQuestions={hideQuestions}
                            closeModal={props.closeModal}
                            applicationVariant={
                                props.leadData.offer?.detail?.region.country !== Country.POLAND ? ApplicationVariant.DEFAULT : applicationVariant
                            }
                            customFormText={props.customFormText}
                            creditData={creditPayload}
                            enablePreferencesStorage={props.leadData.offer?.detail?.region.country === Country.POLAND}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// Styles
const scrollFormWrapStyle = css({
    height: "calc(100% - 6.5rem)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",

    "&::-webkit-scrollbar-track": {
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
        backgroundColor: "var(--colors-gray-400)"
    },

    "&::-webkit-scrollbar": {
        width: "6px",
        backgroundColor: "var(--colors-gray-400)",
        paddingLeft: "2px"
    },

    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--colors-gray-600)"
    }
});

const propertyPriceOverlayStyle = css({
    md: {
        maxHeight: "100vh",
        overflow: "hidden"
    }
});

const contentWrapperStyle = css({
    padding: 0
});
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@panda/css";

import {handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {isEqual} from "@pg-mono/nodash";

import {authFailedButUnlockPrices} from "../../auth/actions/auth_failed_but_unlock_prices_event";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorInstalmentEventAction} from "../../credit_calculator/tracking/credit_gtm";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {setSentLeadValues} from "../../lead/reducers/sent_lead_reducer";
import {IRealEstateIds} from "../../lead/types/IRealEstateIds";
import {calculateRoomsField as calculateRoomsFieldUtil} from "../../lead/utils/calculate_room_fields";
import {getLeadAnalyticsData} from "../../lead/utils/get_lead_analytics_data";
import {getParsedLeadFormValues} from "../../lead/utils/get_parsed_lead_form_values";
import {sendLead} from "../../lead/utils/send_lead";
import {getSentLeadFormValuesFromStorage} from "../../lead/utils/sent_lead_form_values";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {hitGTMEcommerceAddToCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {IAppDispatch, IRootState} from "../../store/store";
import {hitRotatingApplicationError} from "../../tracking/algolytics_hits/application_hit";
import {gtmCheckout} from "../../tracking/google_tag_manager/gtm_checkout";
import {gtmProductAdd} from "../../tracking/google_tag_manager/gtm_product_add";
import {selectGetIsUserAuthenticated} from "../../user/utils/select_get_is_user_authenticated";
import {selectUserProfileData} from "../../user/utils/select_user_profile_data";
import {resetApplicationPost} from "../actions/application_form_actions";
import {initialEmptyApplicationValues} from "../constants/application_form";
import {useApplicationVariant} from "../hooks/use_application_variant";
import {IApplicationData} from "../reducers/application_reducer";
import {hitLeadSent} from "../tracking/hit_lead_sent";
import {IApplicationAnalyticsData, openApplicationFormHit} from "../tracking/tracking";
import {ApplicationVariant} from "../types/ApplicationVariant";
import {IApplication} from "../types/IApplication";
import {IApplicationFormValues} from "../types/IApplicationFormValues";
import {MessageType} from "../types/MessageType";
import {alertFormErrorMessage, alertFormSuccessMessage} from "../utils/alert_text";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getGeneratedMessage} from "../utils/text_generator/get_generated_message";
import {ApplicationForm} from "./application_form/ApplicationForm";
import {IApplicationAdditionalQuestionTypes} from "./application_form/ApplicationFormContext";

type IApplicationOtherProps = {
    initialLeadFormValues: IApplicationFormValues;
    leadData: IApplicationData;
    onSuccessfulSubmit?: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedParams?: Record<string, string>
    ) => void;
    storeHash: string;
    // application input data
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    alwaysShowFields?: boolean;
    hideRodo?: boolean;
    className?: string;
    forceClose?: boolean; // close without any additional modals
};

export type IApplicationOwnProps = IRealEstateIds & IApplicationOtherProps;

type IApplicationProps = IApplicationOwnProps & {
    closeModal: (forceClose?: boolean) => void;
    customFormText?: string;
};

export const Application: React.FC<IApplicationProps> = (props) => {
    const dispatch = useDispatch<IAppDispatch>();
    const location = useLocation();
    const applicationVariant = useApplicationVariant();

    // Redux selectors
    const {affiliation, creditPayload, salesOfficeId, hideQuestions: hideQuestionsFromState} = useAppSelector((state) => state.mainLead);
    const hideQuestions = hideQuestionsFromState || props.leadData.offer?.detail?.region.country !== Country.POLAND;
    const userPreferencesFilters = useSelector((state: IRootState) => state.user.preferences?.data?.filters || {});
    const isAuthenticated = useSelector(selectGetIsUserAuthenticated);
    const userProfileData = useSelector(selectUserProfileData);
    const viewType = useSelector((state: IRootState) => state.viewType.current);

    // Local state
    const [formPreviouslySent, setFormPreviouslySent] = useState(false);
    const [initialFormState, setInitialFormState] = useState<IApplicationFormValues>(props.initialLeadFormValues);

    const [propertyPriceState] = useState<{showPropertyPriceModal: boolean; userDecision: null}>({
        showPropertyPriceModal: false,
        userDecision: null
    });
    const [questions, setQuestions] = useState<IApplicationAdditionalQuestionTypes[]>([]);
    const [messageType, setMessageType] = useState<MessageType>(hideQuestions ? MessageType.CUSTOM : MessageType.GENERATED);
    const [leadIsSubmitting, setLeadIsSubmitting] = useState(false);

    const afterSuccessTimeoutRef = useRef<number>(0);
    const wasInteractionHitRef = useRef(false);

    // Helper functions
    const calculateRoomsField = useCallback(
        (offer: IOfferDetailApplication | undefined) => {
            return calculateRoomsFieldUtil({
                offer,
                propertyId: props.propertyId,
                userPreferencesFilters
            });
        },
        [props.propertyId, userPreferencesFilters]
    );

    const getClearFormState = useCallback(
        (offer: IOfferDetailApplication | undefined) => {
            return {
                initialFormState: {
                    ...initialEmptyApplicationValues,
                    rooms: calculateRoomsField(offer),
                    text: props.customFormText
                }
            };
        },
        [calculateRoomsField, props.customFormText]
    );

    const getAnalyticsData = useCallback((): IApplicationAnalyticsData => {
        return getLeadAnalyticsData(props.leadData, viewType, props.source, props.sourceSection);
    }, [props.leadData, viewType, props.source, props.sourceSection]);

    const onInteraction = useCallback(
        (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
            if (!wasInteractionHitRef.current) {
                wasInteractionHitRef.current = true;

                gtmCheckout(props.leadData, viewType, "standard");
                hitGTMEcommerceBeginCheckout(props.leadData, viewType, "standard", props.sourceSection, formFillAction);
            }
        },
        [props.leadData, viewType, props.sourceSection]
    );

    const onSubmit = useCallback(
        async (values: IApplicationFormValues, applicationData: IApplicationData) => {
            // TODO: Until we rewrite/remove Application, keep this inline with use_lead_modal_send_main_lead
            return await sendLead({
                dispatch: dispatch,
                leadFormValues: values,
                realEstateIds: {
                    vendorId: props.vendorId,
                    offerId: props.offerId,
                    propertyId: props.propertyId,
                    salesOfficeId: salesOfficeId || undefined
                },
                storeHash: props.storeHash,
                leadMeta: {
                    source: props.source,
                    sourceSection: props.sourceSection,
                    locationHash: location.hash,
                    viewType: viewType
                },
                leadType: "standard",
                leadData: applicationData,
                isAuthenticated: isAuthenticated,
                userProfileData: userProfileData,
                senderState: {
                    generatedMessage: null,
                    questions: questions
                },
                analyticsData: getAnalyticsData(),
                affiliation: affiliation || undefined,
                creditData: creditPayload,
                onSendSuccess: async (responseApplication) => {
                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant !== ApplicationVariant.CREDIT) {
                        const hasMktOptin = Boolean(responseApplication.marketing_email || responseApplication.marketing_sms);
                        hitLeadSent(hasMktOptin);
                    }

                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant === ApplicationVariant.CREDIT) {
                        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                            credit_action: CreditCalculatorInstalmentEventAction.CALL
                        });
                    }

                    // Trigger the authentication procedure after sending the application, including setting appropriate A/B test variants
                    dispatch(authFailedButUnlockPrices());

                    props.onSuccessfulSubmit?.(responseApplication, applicationData.offer?.detail, applicationData.property?.detail);
                },
                postOptions: {
                    on400ApplicationBlockedError: (error) => {
                        props.closeModal(true);
                        addWarningNotification("Przepraszamy, oferta na którą chcesz wysłać wiadomość została zarchiwizowana.", 7000);
                        hitRotatingApplicationError(props.source, props.sourceSection, applicationData, {
                            appError: error,
                            extraPayloadViewType: viewType ?? undefined
                        });
                        // TODO: remove this bugsnag notification when we confirm that blocking applications this way is not an issue
                        notifyBugsnag({name: "Application blocked info", message: "Application blocked info"}, "Application sent on an archived offer", {
                            error
                        });
                        setTimeout(() => window.location.reload(), 6000);
                    },
                    on400Response: (error) => {
                        hitRotatingApplicationError(props.source, props.sourceSection, applicationData, {
                            appError: error,
                            extraPayloadViewType: viewType ?? undefined
                        });
                    }
                }
            });
        },
        [dispatch, props, location.hash, viewType, isAuthenticated, userProfileData, questions, getAnalyticsData]
    );

    const onFormikSubmit: IFormikSubmitFn<IApplicationFormValues> = useCallback(
        (values, _formikHelpers) => {
            setLeadIsSubmitting(true);

            // TODO: Until we rewrite/remove Application, keep this inline with use_lead_modal_send_main_lead
            const {offer, property} = props.leadData;

            const offerName = offer?.detail ? offer.detail.name : "";

            const completedValues = {
                ...values,
                text:
                    props.customFormText ||
                    values.text ||
                    getGeneratedMessage({
                        offerType: offer?.detail?.type,
                        offerName: offerName,
                        propertyNumber: property?.detail?.number,
                        name: values.name,
                        rooms: property?.detail?.rooms,
                        area: property?.detail?.area
                    })
            };

            const parsedLeadFormValues = getParsedLeadFormValues(completedValues);
            dispatch(
                setSentLeadValues({
                    ...initialEmptyApplicationValues,
                    ...parsedLeadFormValues,
                    questions: questions
                })
            );

            handleFormikPost(onSubmit(completedValues, props.leadData), _formikHelpers, {
                onSuccess: () => {
                    addSuccessNotification(alertFormSuccessMessage);
                    setLeadIsSubmitting(false);
                    setInitialFormState(getClearFormState(props.leadData.offer?.detail).initialFormState);
                },
                onFieldError: (fieldErrors) => {
                    setLeadIsSubmitting(false);
                    if (!fieldErrors?.email && !fieldErrors?.phone) {
                        addWarningNotification(alertFormErrorMessage);
                    }
                },
                onNonFieldError: () => {
                    setLeadIsSubmitting(false);
                    addWarningNotification(alertFormErrorMessage);
                }
            });
        },
        [props.leadData, props.customFormText, questions, dispatch, onSubmit, addSuccessNotification, getClearFormState, addWarningNotification]
    );

    // componentDidMount
    useEffect(() => {
        const sentLeadFormValues = getSentLeadFormValuesFromStorage();

        // TODO: check formPreviouslySent across application, probably sentLead from redux should be used instead

        setFormPreviouslySent(Boolean(sentLeadFormValues));

        // track form opening
        openApplicationFormHit(getAnalyticsData());
        gtmProductAdd(props.leadData, viewType, "standard");
        hitGTMEcommerceAddToCart(props.leadData, viewType, "standard", props.sourceSection);

        const {name, email, phone} = initialFormState;
        if (name || email || phone?.number) {
            // form will be filled with data automatically
            onInteraction("autofill");
        }
    }, []);

    // componentDidUpdate
    useEffect(() => {
        const equal = isEqual(props.initialLeadFormValues, initialFormState);
        if (!equal) {
            setInitialFormState(props.initialLeadFormValues);
        }
    }, [props.initialLeadFormValues]);

    // componentWillUnmount
    useEffect(() => {
        return () => {
            dispatch(resetApplicationPost(props.storeHash));
            clearTimeout(afterSuccessTimeoutRef.current);
            afterSuccessTimeoutRef.current = -1; // do not allow to setTimeout after this assignment
        };
    }, [props.storeHash]);

    const {showPropertyPriceModal} = propertyPriceState;

    return (
        <div className={scrollFormWrapStyle}>
            {initialFormState && (
                <div className={showPropertyPriceModal ? propertyPriceOverlayStyle : ""}>
                    {/* hideRooms: we show rooms in right column (in form) when we do not ask for property and we consider promotion or vendor (no offerId) application*/}
                    <div className={contentWrapperStyle}>
                        <ApplicationForm
                            leadIsSubmitting={leadIsSubmitting}
                            onSubmit={onFormikSubmit}
                            initialValues={initialFormState}
                            onFormControlClick={onInteraction}
                            formPreviouslySent={formPreviouslySent}
                            hideRooms={!!props.propertyId}
                            hideMessageSection={false}
                            offerId={props.offerId}
                            alwaysShowFields={props.alwaysShowFields} // For this test - when form is in modal, we do not hide fields.
                            storeHash={props.storeHash}
                            hideSubmitButton={false}
                            source={props.source}
                            sourceSection={props.sourceSection}
                            hideRodo={props.hideRodo}
                            applicationMessageLogic={{
                                currentState: {
                                    selectedQuestions: questions,
                                    messageType: messageType
                                },
                                helpers: {
                                    setMessageType: (newMessageType: MessageType) => {
                                        setMessageType(newMessageType);
                                    },
                                    setSelectedQuestions: (questionsIds: IApplicationAdditionalQuestionTypes[]) => {
                                        setQuestions(questionsIds);
                                    }
                                }
                            }}
                            getAnalyticsData={getAnalyticsData}
                            hideQuestions={hideQuestions}
                            closeModal={props.closeModal}
                            applicationVariant={
                                props.leadData.offer?.detail?.region.country !== Country.POLAND ? ApplicationVariant.DEFAULT : applicationVariant
                            }
                            customFormText={props.customFormText}
                            creditData={creditPayload}
                            enablePreferencesStorage={props.leadData.offer?.detail?.region.country === Country.POLAND}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// Styles
const scrollFormWrapStyle = css({
    height: "calc(100% - 6.5rem)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",

    "&::-webkit-scrollbar-track": {
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
        backgroundColor: "var(--colors-gray-400)"
    },

    "&::-webkit-scrollbar": {
        width: "6px",
        backgroundColor: "var(--colors-gray-400)",
        paddingLeft: "2px"
    },

    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--colors-gray-600)"
    }
});

const propertyPriceOverlayStyle = css({
    md: {
        maxHeight: "100vh",
        overflow: "hidden"
    }
});

const contentWrapperStyle = css({
    padding: 0
});
